import { useDroppable } from '@dnd-kit/react';
import { pointerDistance, pointerIntersection } from "@dnd-kit/collision";
import type { CollisionDetector } from "@dnd-kit/abstract";

type GapDropZoneProps = {
  id: string;
  parentGroupId: string | null;
  insertBeforeId?: string;
  accept: (source: any) => boolean;
  variant?: 'bar' | 'empty-section';
};

export const GapDropZone = ({ id, parentGroupId, insertBeforeId, accept, variant = 'bar' }: GapDropZoneProps) => {
  const { ref, isDropTarget } = useDroppable({
    id,
    data: { type: 'gap', parentGroupId, insertBeforeId },
    collisionDetector: nearbyCollisionDetector,
    accept,
  });

  if (variant === 'empty-section') {
    return (
      <div
        ref={ref}
        data-testid={`bundle-drop-gap-${id}`}
        className={`px-3 py-3 text-xs text-center text-gray-400 rounded-b-lg transition-colors ${isDropTarget ? 'ring-2 ring-inset ring-crimson-500' : ''}`}
      >
        No documents in this group
      </div>
    );
  }

  return (
    <div
      ref={ref}
      data-testid={`bundle-drop-gap-${id}`}
      className={`w-full rounded-full h-[3px] -mb-1 transition-colors ${isDropTarget ? 'relative z-10 bg-crimson-500' : ''}`}
    />
  );
};

/**
 * Custom collision detector: tries pointerIntersection first, then falls back to a
 * distance-based collision for droppables within 50px of the pointer.
 *
 * The gap drop zones we use are 3px tall, so precise pointer intersection rarely
 * hits them. The fallback returns a collision whose `value` decreases with pointer
 * distance to the droppable's rect — the closest accepted droppable wins. This is
 * why dropping anywhere in the padding below the last row still registers on the
 * email-group tail gap; a plain shape-overlap fallback wouldn't, since a 3px gap
 * and the dragged row rarely overlap once the cursor has moved past the group.
 */
export const nearbyCollisionDetector: CollisionDetector = (input) => {
  const result = pointerIntersection(input);
  if (result) return result;

  const pointer = input.dragOperation.position.current;
  if (!pointer || !input.droppable.shape) return null;

  const rect = input.droppable.shape.boundingRectangle;
  const distX = Math.max(0, rect.left - pointer.x, pointer.x - rect.right);
  const distY = Math.max(0, rect.top - pointer.y, pointer.y - rect.bottom);
  if (distX > 50 || distY > 50) return null;

  return pointerDistance(input);
};

/** True if dropping `sourceId` at the gap position described by `insertBeforeId` would
 *  leave it in the same place — i.e. the drop is redundant. Returns false if `sourceId`
 *  isn't in `childIds` at all (moving from elsewhere is always a real move). */
export function isNoOpDrop(sourceId: string, childIds: string[], insertBeforeId?: string): boolean {
  const sourceIdx = childIds.indexOf(sourceId);
  if (sourceIdx === -1) return false;
  if (!insertBeforeId) return sourceIdx === childIds.length - 1;
  const targetIdx = childIds.indexOf(insertBeforeId);
  return sourceIdx === targetIdx || sourceIdx === targetIdx - 1;
}

/** Synchronous "nearest accepted droppable within `maxDistance`px" lookup used as a fallback
 *  when @dnd-kit's async `operation.target` hasn't resolved by the time `pointerup` fires
 *  (common on fast drops). Mirrors the 50px policy of `nearbyCollisionDetector` — only
 *  droppables whose `accepts(source)` passes are considered. */
export function findNearestAcceptedDroppable(
  manager: any,
  source: any,
  pointer: { x: number; y: number },
  maxDistance = 50,
): any | null {
  const droppables = manager?.registry?.droppables;
  if (!droppables) return null;
  let best: any = null;
  let bestDistSq = Infinity;
  for (const d of droppables) {
    if (d.disabled) continue;
    if (!d.accepts(source)) continue;
    const rect = d.shape?.boundingRectangle;
    if (!rect) continue;
    const dx = Math.max(0, rect.left - pointer.x, pointer.x - rect.right);
    const dy = Math.max(0, rect.top - pointer.y, pointer.y - rect.bottom);
    if (dx > maxDistance || dy > maxDistance) continue;
    const distSq = dx * dx + dy * dy;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      best = d;
    }
  }
  return best;
}
